import { Injectable } from '@angular/core';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { DisplayService } from './display.service';
import { FirmwareUpdateRequesterService } from './firmware-update-requester.service';
import { FirmwareUpdateNotificationService } from './firmware-update-notification.service';

/**
 * FirmwareUpdateService
 *
 * Service to handle firmware update requests for LG webOS Player and
 * Amazon Signage Stick displays. The backend requires a valid URL ending
 * with .epk for every request, regardless of player type. LG webOS sends
 * the file to the device for upgrade (e.g. from Rise Vision Storage or
 * another host); Signage Stick ignores the URL's content and triggers its
 * own OTA update check on the device.
 *
 * Follows the same pattern as ScreenshotService using Primus connections.
 * Supports both single display and batch firmware updates. Uses Primus
 * (WebSocket) to communicate with Core and receive real-time status updates.
 */
@Injectable({
  providedIn: 'root',
})
export class FirmwareUpdateService {
  firmwareUpdateInProgress?: boolean;
  firmwareUpdateError?: string;

  constructor(
    private displayService: DisplayService,
    private displayApiService: DisplayApiService,
    private firmwareUpdateRequesterService: FirmwareUpdateRequesterService,
    private firmwareUpdateNotificationService: FirmwareUpdateNotificationService,
  ) {}

  /**
   * Request firmware update for a single display.
   *
   * @param displayId The display ID to update
   * @param displayName The display name for notification
   * @param firmwareUrl Any valid URL ending with .epk (sent to the device for upgrade)
   * @returns Promise that resolves when firmware update completes or rejects on error
   */
  requestFirmwareUpdate(
    displayId: string,
    displayName: string,
    firmwareUrl: string,
  ): Promise<any> {
    if (!firmwareUrl || !firmwareUrl.endsWith('.epk')) {
      return Promise.reject(
        new Error(
          'Invalid firmware URL. Must be a valid URL ending with .epk.',
        ),
      );
    }

    this.firmwareUpdateInProgress = true;
    this.firmwareUpdateError = undefined;

    return this.firmwareUpdateRequesterService
      .request((clientId) => {
        console.log('Firmware update clientId', clientId);
        return this.displayApiService.requestFirmwareUpdate(
          displayId,
          clientId,
          firmwareUrl,
        );
      })
      .then(() => {
        this.firmwareUpdateInProgress = false;
        // Add success notification
        this.firmwareUpdateNotificationService.addSingleUpdateNotification(
          displayName,
        );
        return Promise.resolve();
      })
      .catch((err) => {
        this.firmwareUpdateInProgress = false;

        if (err === 'timeout') {
          this.firmwareUpdateError =
            'Firmware update timed out. The update may still be in progress on the player.';
        } else if (err === 'firmware-upgrade-failed') {
          this.firmwareUpdateError = 'Firmware update failed on the player.';
        } else {
          this.firmwareUpdateError = 'Error requesting firmware update.';
        }

        console.error('Error requesting firmware update', err);
        return Promise.reject(err);
      });
  }

  /**
   * Request firmware update for multiple displays (batch update)
   *
   * @param displays Array of display objects with id and name
   * @param firmwareUrl Any valid URL ending with .epk (sent to each device for upgrade)
   * @returns Promise that resolves when all firmware updates are initiated
   */
  requestBatchFirmwareUpdate(
    displays: Array<{ id: string; name: string }>,
    firmwareUrl: string,
  ): Promise<any> {
    if (!firmwareUrl || !firmwareUrl.endsWith('.epk')) {
      return Promise.reject(
        new Error(
          'Invalid firmware URL. Must be a valid URL ending with .epk.',
        ),
      );
    }

    this.firmwareUpdateInProgress = true;
    this.firmwareUpdateError = undefined;

    // Add batch notification upfront
    this.firmwareUpdateNotificationService.addBatchUpdateNotification(
      displays.length,
    );

    // Send firmware update requests for all displays in parallel
    const updatePromises = displays.map((display) =>
      this.firmwareUpdateRequesterService
        .request((clientId) => {
          console.log(`Firmware update clientId for ${display.name}`, clientId);
          return this.displayApiService.requestFirmwareUpdate(
            display.id,
            clientId,
            firmwareUrl,
          );
        })
        .catch((err) => {
          console.error(
            `Error requesting firmware update for ${display.name}`,
            err,
          );
          // Don't fail the whole batch if one fails
          return Promise.resolve();
        }),
    );

    return Promise.all(updatePromises)
      .then(() => {
        this.firmwareUpdateInProgress = false;
        return Promise.resolve();
      })
      .catch((err) => {
        this.firmwareUpdateInProgress = false;
        this.firmwareUpdateError = 'Error requesting batch firmware update.';
        console.error('Error requesting batch firmware update', err);
        return Promise.reject(err);
      });
  }
}
